/**
 * Exposure and risk screens.
 *
 * The recurring idea: **never show a number without the reasoning that produced it.** A ranking an
 * asset owner cannot reconstruct is a ranking they will dispute, and the first dispute the model
 * loses is the last time anyone uses it. Every finding therefore carries its formula, and the
 * leaderboard says what fixing a CVE would actually remove.
 */

import type { JSX } from 'react';

import { api } from '../shared/api/client';
import {
  Badge,
  Card,
  Empty,
  ErrorBox,
  Formula,
  PageHeader,
  severityTone,
  Spinner,
  Stat,
  StatRow,
  Table,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

export function Vulnerabilities(): JSX.Element {
  const { data, error, loading } = useAsync(() => api.vulnerabilities(200));

  const open = data ?? [];
  const critical = open.filter((f) => f.severity === 'critical').length;
  const exploited = open.filter((f) => f.known_exploited).length;
  const shadow = open.filter((f) => f.ci_id === null).length;

  return (
    <>
      <PageHeader
        title="Vulnerabilities"
        subtitle="Open findings, ranked by the risk they carry on this estate — not by CVSS alone."
      />

      <ErrorBox error={error} />

      <Card>
        <StatRow>
          <Stat label="Open findings" value={open.length} />
          <Stat label="Critical" value={critical} tone={critical > 0 ? 'danger' : 'neutral'} />
          <Stat
            label="Known exploited"
            value={exploited}
            tone={exploited > 0 ? 'danger' : 'neutral'}
            hint="observed in the wild"
          />
          <Stat
            label="On unknown assets"
            value={shadow}
            tone={shadow > 0 ? 'warn' : 'neutral'}
            hint="the CMDB has drifted"
          />
        </StatRow>
      </Card>

      <Card
        title="Ranked findings"
        subtitle="Each score shows its working. The same CVE ranks differently on different assets, because the CMDB knows what each asset is."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Risk', 'Severity', 'CVE', 'Asset', 'Finding']}
            empty="No open findings. Import a scan to populate this."
          >
            {open.map((f) => (
              <tr key={f.id}>
                <td className="table__num table__strong">{f.risk_score ?? '—'}</td>
                <td>
                  <Badge tone={severityTone(f.severity)}>{f.severity}</Badge>
                  {f.known_exploited && (
                    <>
                      {' '}
                      <Badge tone="danger" title="Known to be exploited in the wild">
                        exploited
                      </Badge>
                    </>
                  )}
                </td>
                <td>{f.cve ?? '—'}</td>
                <td>
                  {f.asset}
                  {f.ci_id === null && (
                    <>
                      {' '}
                      <Badge tone="warn" title="This asset is not in the CMDB">
                        shadow
                      </Badge>
                    </>
                  )}
                </td>
                <td>
                  <div className="table__strong">{f.title}</div>
                  {f.risk_explanation !== null && <Formula>{f.risk_explanation}</Formula>}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

export function Leaderboard(): JSX.Element {
  const { data, error, loading } = useAsync(() => api.leaderboard(25));
  const rows = data ?? [];

  return (
    <>
      <PageHeader
        title="Fix first"
        subtitle="The few patches that remove the most risk. Ordered by what a maintenance window would actually buy you."
      />

      <ErrorBox error={error} />

      <Card
        title="Risk removed if fixed"
        subtitle="Grouped by CVE across every affected asset, because a patch is applied per CVE and not per finding."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['CVE', 'Risk removed', 'Assets', 'Findings', 'Worst single', '']}
            empty="Nothing to rank yet."
          >
            {rows.map((e) => (
              <tr key={e.cve}>
                <td className="table__strong">{e.cve}</td>
                <td className="table__num table__strong">{e.risk_removed_if_fixed}</td>
                <td className="table__num">{e.affected_assets}</td>
                <td className="table__num">{e.affected_findings}</td>
                <td className="table__num">{e.peak_risk}</td>
                <td>
                  {e.known_exploited && <Badge tone="danger">exploited in the wild</Badge>}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

export function ShadowAssets(): JSX.Element {
  const { data, error, loading } = useAsync(() => api.shadowAssets(200));
  const rows = data ?? [];

  return (
    <>
      <PageHeader
        title="Shadow assets"
        subtitle="Hosts a scanner found that the CMDB does not know about."
      />

      <ErrorBox error={error} />

      <Card
        title="Why this matters twice"
        subtitle="Access scope is derived from the CMDB, so an asset missing from it is both an unpatched machine and one nobody can be authorized to reach through the normal path."
      >
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty>
            Every scanned asset resolved to a configuration item. That is the state you want.
          </Empty>
        ) : (
          <Table columns={['Asset', 'Open findings']}>
            {rows.map((a) => (
              <tr key={a.asset}>
                <td className="table__strong">{a.asset}</td>
                <td className="table__num">{a.open_findings}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

export function RiskMethodology(): JSX.Element {
  const { data, error, loading } = useAsync(() => api.riskConfig());

  return (
    <>
      <PageHeader
        title="Risk methodology"
        subtitle="The formula behind every score. Yours to change — the model is only useful if you agree with it."
      />

      <ErrorBox error={error} />

      {loading ? (
        <Spinner />
      ) : data === null ? (
        <Empty>No configuration returned.</Empty>
      ) : (
        <>
          <Card>
            <StatRow>
              <Stat
                label="Methodology version"
                value={data.version}
                hint="stamped onto every score computed under it"
              />
            </StatRow>
          </Card>

          <Card
            title="Base points, by severity"
            subtitle="The starting value before any factor applies."
          >
            <Table columns={['Severity', 'Base']}>
              {(['critical', 'high', 'medium', 'low', 'info'] as const).map((sev) => (
                <tr key={sev}>
                  <td>
                    <Badge tone={severityTone(sev)}>{sev}</Badge>
                  </td>
                  <td className="table__num table__strong">
                    {num(data.config[`base_${sev}`])}
                  </td>
                </tr>
              ))}
            </Table>
          </Card>

          <Card
            title="Factors"
            subtitle="Each multiplies the base when it applies. A factor can be switched off without losing its definition, so 'we do not count this' stays distinguishable from 'we never considered it'."
          >
            <Table columns={['Factor', 'Multiplier', 'Enabled']}>
              {FACTORS.map((f) => (
                <tr key={f.key}>
                  <td>
                    <div className="table__strong">{f.label}</div>
                    <Formula>{f.note}</Formula>
                  </td>
                  <td className="table__num">{num(data.config[f.key])}</td>
                  <td>
                    {enabled(data.config, f.toggle) ? (
                      <Badge tone="ok">on</Badge>
                    ) : (
                      <Badge tone="muted">off</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

const FACTORS = [
  {
    key: 'exploit_multiplier',
    toggle: 'exploit',
    label: 'Known exploited',
    note: 'the vulnerability is being used in the wild',
  },
  {
    key: 'epss_multiplier',
    toggle: 'epss',
    label: 'Exploit prediction',
    note: 'EPSS at or above the threshold',
  },
  {
    key: 'internet_multiplier',
    toggle: 'internet',
    label: 'Internet exposed',
    note: 'the asset is reachable from outside',
  },
  {
    key: 'telemetry_multiplier',
    toggle: 'telemetry',
    label: 'Telemetry corroborated',
    note: 'observed log activity supports it',
  },
  {
    key: 'posture_poor_multiplier',
    toggle: 'posture',
    label: 'Poor hardening',
    note: 'a weakly hardened host makes its vulnerabilities easier to reach',
  },
  {
    key: 'mitigation_multiplier',
    toggle: 'mitigation',
    label: 'Verified remediation',
    note: 'reduces to residual risk; fully reversible',
  },
] as const;

/**
 * Renders a configuration value that arrived as `unknown`.
 *
 * `String(value)` would render an unexpected object as "[object Object]", which tells the reader
 * nothing and hides that the stored configuration has a shape this build does not understand.
 */
function num(value: unknown): string {
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  return '—';
}

function enabled(config: Record<string, unknown>, key: string): boolean {
  const toggles = config['enabled'];
  if (typeof toggles !== 'object' || toggles === null) return false;
  return (toggles as Record<string, unknown>)[key] === true;
}
